import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useCartExtras, type SaveSource } from '../../store/cartExtras';
import { CartModal } from './CartModal';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * "Save my order" — email capture → POST /projects/:id/save. Success shows the
 * 30-day/resume-anywhere reassurance; an already-saved order opens prefilled
 * with resend/update semantics.
 */
export function SaveOrderModal({
  onClose,
  source = 'cart',
}: {
  onClose: () => void;
  /** Which surface opened this — rides the pp_order_saved event. */
  source?: SaveSource;
}) {
  const savedEmail = useCartExtras((s) => s.savedEmail);
  const saving = useCartExtras((s) => s.saving);
  const saveOrder = useCartExtras((s) => s.saveOrder);

  const [email, setEmail] = useState(savedEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const alreadySaved = !!savedEmail;
  const emailChanged = alreadySaved && email.trim().toLowerCase() !== savedEmail!.toLowerCase();

  async function submit() {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError('That doesn’t look like an email address. Check it and try again.');
      return;
    }
    setError(null);
    try {
      await saveOrder(value, source);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : "We couldn't save your order. Check your connection and try again.",
      );
    }
  }

  if (done) {
    return (
      <CartModal title="Order saved" onClose={onClose}>
        <div className="savemodal">
          <div className="savemodal__done">
            <span className="savemodal__tick">✓</span>
            <p className="savemodal__body">
              We&rsquo;ll keep this order for <strong>30 days</strong>. The resume link we just
              sent to <strong>{email.trim()}</strong> works on any device.
            </p>
          </div>
          <div className="cmodal__actions">
            <button type="button" className="btn btn--primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </CartModal>
    );
  }

  return (
    <CartModal
      title={alreadySaved ? 'Resend or update your save' : 'Save my order'}
      caption={
        alreadySaved
          ? `This order is saved to ${savedEmail}.`
          : 'Your photos, crops and sizes are kept for 30 days — we’ll email a link to pick up right where you left off, on any device.'
      }
      onClose={onClose}
    >
      <form
        className="savemodal"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="savemodal__field">
          <span className="savemodal__label">Email</span>
          <input
            className={`savemodal__input${error ? ' savemodal__input--err' : ''}`}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            placeholder="you@example.com"
            autoFocus
            required
          />
        </label>
        {error && (
          <div className="savemodal__error" role="alert">
            {error}
          </div>
        )}
        <p className="savemodal__privacy">
          We&rsquo;ll only use this to send your resume link and order updates. No marketing, no
          sharing.
        </p>
        <div className="cmodal__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving
              ? 'Saving…'
              : alreadySaved
                ? emailChanged
                  ? 'Update & resend link'
                  : 'Resend link'
                : 'Save order'}
          </button>
        </div>
      </form>
    </CartModal>
  );
}
